// Helpers
import NFA from 'shared/helpers/analytics/NFAnalytics';
import { getWebAppType } from 'shared/helpers/deviceType/webAppType';

// Constants
import { TRACK_TYPE } from 'shared/helpers/analytics/constants';
import { WEB_APP_TYPE } from 'shared/constants';

// Event names for image search
export const EVENT_NAME = {
  IMAGE_SEARCH_INITIATED: 'image_search_initiated',
  IMAGE_CAPTURED: 'image_captured',
  IMAGE_UPLOADED: 'image_uploaded',
  IMAGE_CROPPED: 'image_cropped',
  AI_PROCESSING_STARTED: 'ai_processing_started',
  AI_PROCESSING_COMPLETED: 'ai_processing_completed',
  SEARCH_RESULTS_VIEWED: 'search_results_viewed',
  PRODUCT_CLICKED: 'image_search_product_clicked',
  FILTER_APPLIED: 'image_search_filter_applied',
  CAMERA_FLIPPED: 'camera_flipped',
  IMAGE_RETAKEN: 'image_retaken',
  SUGGESTION_CHIP_CLICKED: 'suggestion_chip_clicked',
};

interface ImageSearchEventData {
  source?: 'camera' | 'upload' | 'gallery';
  device_type?: 'mobile' | 'desktop';
  ai_source?: 'builtin' | 'api';
  processing_time?: number;
  image_size?: number;
  crop_dimensions?: { width: number; height: number; x: number; y: number };
  search_term?: string;
  results_count?: number;
  position?: number;
  product_id?: string;
  filter_type?: string;
  filter_value?: string;
  chip_type?: string;
  chip_value?: string;
  facing_mode?: 'user' | 'environment';
  error_message?: string;
  result?: string;
}

// Utility function to track image search events
export const trackImageSearchEvent = (
  eventName: string,
  eventData: ImageSearchEventData = {},
  isMobileApp: boolean = false
) => {
  const type = getWebAppType();
  const enhancedData = {
    ...eventData,
    timestamp: Date.now(),
    page_type: 'image_search',
  };

  if (WEB_APP_TYPE.ANDROID === type && isMobileApp && window?.AndroidFunction?.doMixpanel) {
    window.AndroidFunction.doMixpanel(eventName, JSON.stringify(enhancedData));
  } else if (WEB_APP_TYPE.IOS === type && isMobileApp) {
    window.webkit.messageHandlers.doMixpanel.postMessage({
      eventName,
      params: JSON.stringify(enhancedData),
    });
  } else {
    NFA.track({
      type: TRACK_TYPE.EVENT,
      event: eventName,
      data: enhancedData,
    });
  }
};

// Track image search initiation
export const onImageSearchInitiated = (
  source: 'camera' | 'upload',
  deviceType: 'mobile' | 'desktop'
) => {
  trackImageSearchEvent(EVENT_NAME.IMAGE_SEARCH_INITIATED, {
    source,
    device_type: deviceType,
  });
};

// Track image capture
export const onImageCaptured = (
  deviceType: 'mobile' | 'desktop',
  facingMode?: 'user' | 'environment'
) => {
  trackImageSearchEvent(EVENT_NAME.IMAGE_CAPTURED, {
    source: 'camera',
    device_type: deviceType,
    facing_mode: facingMode,
  });
};

// Track image upload
export const onImageUploaded = (deviceType: 'mobile' | 'desktop', imageSize?: number) => {
  trackImageSearchEvent(EVENT_NAME.IMAGE_UPLOADED, {
    source: 'upload',
    device_type: deviceType,
    image_size: imageSize,
  });
};

// Track image cropping
export const onImageCropped = (cropDimensions: {
  width: number;
  height: number;
  x: number;
  y: number;
}) => {
  trackImageSearchEvent(EVENT_NAME.IMAGE_CROPPED, {
    crop_dimensions: cropDimensions,
  });
};

// Track AI processing
export const onAIProcessingStarted = (aiSource: 'builtin' | 'api') => {
  trackImageSearchEvent(EVENT_NAME.AI_PROCESSING_STARTED, {
    ai_source: aiSource,
  });
};

export const onAIProcessingCompleted = (
  aiSource: 'builtin' | 'api',
  processingTime: number,
  searchTerm?: string,
  result?: string
) => {
  trackImageSearchEvent(EVENT_NAME.AI_PROCESSING_COMPLETED, {
    ai_source: aiSource,
    processing_time: processingTime,
    search_term: searchTerm,
    result: result,
  });
};

// Track search results
export const onSearchResultsViewed = (resultsCount: number, searchTerm?: string) => {
  trackImageSearchEvent(EVENT_NAME.SEARCH_RESULTS_VIEWED, {
    results_count: resultsCount,
    search_term: searchTerm,
  });
};

// Track product clicks
export const onProductClicked = (productId: string, position: number, searchTerm?: string) => {
  trackImageSearchEvent(EVENT_NAME.PRODUCT_CLICKED, {
    product_id: productId,
    position: position,
    search_term: searchTerm,
  });
};

// Track filter application
export const onFilterApplied = (filterType: string, filterValue: string) => {
  trackImageSearchEvent(EVENT_NAME.FILTER_APPLIED, {
    filter_type: filterType,
    filter_value: filterValue,
  });
};

// Track camera flip
export const onCameraFlipped = (newFacingMode: 'user' | 'environment') => {
  trackImageSearchEvent(EVENT_NAME.CAMERA_FLIPPED, {
    facing_mode: newFacingMode,
  });
};

// Track image retake
export const onImageRetaken = (deviceType: 'mobile' | 'desktop') => {
  trackImageSearchEvent(EVENT_NAME.IMAGE_RETAKEN, {
    device_type: deviceType,
  });
};

// Track suggestion chip clicks
export const onSuggestionChipClicked = (chipType: string, chipValue: string) => {
  trackImageSearchEvent(EVENT_NAME.SUGGESTION_CHIP_CLICKED, {
    chip_type: chipType,
    chip_value: chipValue,
  });
};
